import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import BrandNavbar from '../components/dashboard/BrandNavbar';
import CreateCampaignModal from '../components/brand/CreateCampaignModal';

const BrandDashboard = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'brand') return;
    const fetchDashboardData = async () => {
      try {
        const response = await api.get(`/brands/${user.userId}/dashboard`);
        const data = response.data;
        setCampaigns(data.projects || []);
        setCreators(data.creators || []);
      } catch (err) {
        // Error handled silently.
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [user]);

  const refreshCampaigns = () => {
    setLoading(true);
    api.get(`/brands/${user.userId}/dashboard`)
      .then((response) => {
        setCampaigns(response.data.projects || []);
        setCreators(response.data.creators || []);
      })
      .catch(() => {
        // Error handled silently.
      })
      .finally(() => setLoading(false));
  };

  if (!user) return <p className="p-8 text-center">Please log in to view your dashboard.</p>;
  if (user.role !== 'brand') return <p className="p-8 text-center">Access denied. Only brands can view this page.</p>;
  if (loading) return <p className="p-8 text-center">Loading your dashboard...</p>;

  return (
    <>
      <BrandNavbar />
      <div className="dashboard-shell min-h-screen pt-24">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
          <section className="app-card rounded-[2rem] p-6 sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="section-kicker mb-4">Brand overview</div>
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Welcome to your brand dashboard</h1>
                <p className="mt-3 text-slate-600 max-w-2xl">
                  Monitor campaign activity, review creator relationships, and move your next launch forward with a clearer control center.
                </p>
              </div>
              <button onClick={() => setShowCreateModal(true)} className="primary-button">
                Create Campaign
              </button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="metric-card">
                <p className="text-sm text-slate-500">Active campaigns</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">{campaigns.length}</p>
                <p className="mt-2 text-sm text-slate-500">Live projects ready for review and bidding.</p>
              </div>
              <div className="metric-card">
                <p className="text-sm text-slate-500">Connected creators</p>
                <p className="mt-2 text-3xl font-bold text-slate-900">{creators.length}</p>
                <p className="mt-2 text-sm text-slate-500">Profiles already linked to your account.</p>
              </div>
              <div className="metric-card">
                <p className="text-sm text-slate-500">Next best action</p>
                <p className="mt-2 text-xl font-bold text-slate-900">Launch or review campaigns</p>
                <p className="mt-2 text-sm text-slate-500">Keep the pipeline moving with one-click actions.</p>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              onClick={() => navigate('/dashboard/brand/live-bids')}
              className="rounded-[1.75rem] bg-gradient-to-r from-blue-600 to-blue-500 text-white p-6 shadow-xl cursor-pointer hover:-translate-y-1 transition-transform"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Live Bids</h3>
                  <p className="text-sm opacity-90">Monitor bids on your campaigns</p>
                </div>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>

            <div
              onClick={() => setShowCreateModal(true)}
              className="rounded-[1.75rem] bg-[linear-gradient(135deg,#539ECB_0%,#3C86B5_100%)] text-white p-6 shadow-xl cursor-pointer hover:-translate-y-1 transition-transform"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Create Campaign</h3>
                  <p className="text-sm opacity-90">Launch a new campaign</p>
                </div>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>

            <div
              onClick={() => navigate('/dashboard/brand/campaigns')}
              className="rounded-[1.75rem] bg-gradient-to-r from-violet-600 to-purple-500 text-white p-6 shadow-xl cursor-pointer hover:-translate-y-1 transition-transform"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Manage Campaigns</h3>
                  <p className="text-sm opacity-90">View and edit your campaigns</p>
                </div>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-2l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <section className="app-card rounded-[2rem] p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Active Campaigns</h2>
              <button
                className="primary-button !px-4 !py-2.5 !text-sm w-full sm:w-auto"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Campaign
              </button>
            </div>

            <div className="hidden md:block">
              <div className="grid grid-cols-[2fr_1fr_1fr] font-semibold border-b border-slate-200 pb-3 mb-2 text-sm text-slate-500 uppercase tracking-wide">
                <div>Campaign Name</div>
                <div>Budget</div>
                <div>Bidding End Date</div>
              </div>
              {campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <div key={campaign.id} className="grid grid-cols-[2fr_1fr_1fr] items-center border-b border-slate-100 py-4 text-sm">
                    <button
                      className="font-semibold text-collaborb-spherical-mid hover:underline text-left"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      onClick={() => navigate(`/dashboard/brand/campaigns/${campaign.id}`)}
                    >
                      {campaign.title}
                    </button>
                    <span className="text-slate-600">Rs. {Number(campaign.budget).toLocaleString('en-IN')}</span>
                    <span className="text-slate-600">
                      {campaign.biddingEndDate
                        ? new Date(campaign.biddingEndDate).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : '--'
                      }
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-8">No active campaigns.</p>
              )}
            </div>

            <div className="md:hidden space-y-3">
              {campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <div key={campaign.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <button
                      className="font-semibold text-collaborb-spherical-mid hover:underline text-left text-base mb-2 block w-full"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      onClick={() => navigate(`/dashboard/brand/campaigns/${campaign.id}`)}
                    >
                      {campaign.title}
                    </button>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Budget:</span>
                        <span className="font-medium">Rs. {Number(campaign.budget).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Bidding End:</span>
                        <span className="font-medium">
                          {campaign.biddingEndDate
                            ? new Date(campaign.biddingEndDate).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : '--'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-8">No active campaigns.</p>
              )}
            </div>
          </section>

          <section className="app-card rounded-[2rem] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">Connected creators</h2>
              <button className="secondary-button !px-4 !py-2.5 !text-sm" onClick={() => navigate('/dashboard/brand/creators')}>
                Discover more
              </button>
            </div>
            {creators.length > 0 ? (
              <div className="grid gap-3 md:grid-cols-2">
                {creators.map((creator) => (
                  <div key={creator.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="font-bold text-slate-900">{creator.name}</p>
                    <p className="text-sm text-slate-600">{creator.email}</p>
                    <p className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      Credits: {creator.credits}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No creators connected yet.</p>
            )}
          </section>
        </div>
      </div>
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={refreshCampaigns}
        userId={user.userId}
      />
    </>
  );
};

export default BrandDashboard;
